import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtMoney } from "../ops/opsUtils";
import { Search, X, FileText, Loader2, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";

/**
 * GeneratePayoffModal
 * Modes:
 *   prefill: { clientName?: string, loanIds?: string[] } — skip search step
 */
export default function GeneratePayoffModal({ open, onClose, prefill = {} }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(prefill.clientName || prefill.loanIds ? "review" : "search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(prefill.clientName || null);
  const [selectedLoanIds, setSelectedLoanIds] = useState(prefill.loanIds || null);
  const [groupOffer, setGroupOffer] = useState(null);   // {active_count, consolidated_total_active, advances, group_id}
  const [consolidate, setConsolidate] = useState(true); // default: consolidated when siblings exist
  const [preview, setPreview] = useState(null);
  const [overrideAmount, setOverrideAmount] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [validDays, setValidDays] = useState(10);
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(prefill.clientName || prefill.loanIds ? "review" : "search");
    setSelectedClient(prefill.clientName || null);
    setSelectedLoanIds(prefill.loanIds || null);
    setQuery(""); setResults([]); setOverrideAmount(""); setOverrideReason(""); setNotes("");
    setTimeout(() => searchRef.current?.focus(), 80);
  }, [open, prefill.clientName, prefill.loanIds?.join(",")]);

  // When invoked from a single-loan page, check whether this loan has active
  // siblings in the same client_group_id — if so, offer consolidation.
  useEffect(() => {
    if (!open) return;
    setGroupOffer(null);
    if (!(prefill.loanIds && prefill.loanIds.length === 1)) return;
    (async () => {
      try {
        const { data } = await api.get(`/operations/payoffs/client-groups/by-loan/${prefill.loanIds[0]}`);
        if (data && data.active_count > 1) {
          setGroupOffer(data);
          setConsolidate(true); // default to consolidated when siblings exist
        }
      } catch (e) { /* non-fatal — fall back to single-loan payoff */ }
    })();
  }, [open, prefill.loanIds?.join(",")]);

  useEffect(() => {
    if (!open) return;
    if (step !== "review") return;
    if (!selectedClient && !selectedLoanIds) return;
    (async () => {
      try {
        // If a group offer is available and the user chose consolidated,
        // preview against the whole group — otherwise preview single loan.
        if (groupOffer && consolidate) {
          const { data } = await api.post(`/operations/payoffs/client-groups/preview-consolidated`,
            { client_group_id: groupOffer.client_group_id });
          const snap = data.snapshot;
          setPreview({
            calculated_payoff: snap.calculated_payoff,
            total_principal: snap.total_principal,
            accrued: (snap.loan_breakdowns || []).reduce((a, b) => a + Number(b.accrued_interest || 0), 0),
            loans: snap.loan_ids.length,
            tranches: snap.tranches.length,
          });
          return;
        }
        if (selectedLoanIds) {
          const loans = [];
          for (const id of selectedLoanIds) {
            const { data } = await api.get(`/operations/loans/${id}`);
            loans.push(data);
          }
          setPreview(buildLocalPreview(loans));
        } else {
          const { data } = await api.get(`/operations/loans`, { params: { search: selectedClient, limit: 5000 } });
          const filtered = data.filter((l) => l.client_name === selectedClient && ["Funded", "Active", "PendingSettlement", "PayoffRequested"].includes(l.current_status));
          setPreview(buildLocalPreview(filtered.length ? filtered : data.filter((l) => l.client_name === selectedClient)));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [open, step, selectedClient, selectedLoanIds, groupOffer, consolidate]);

  // Debounced search
  useEffect(() => {
    if (!open || step !== "search") return;
    clearTimeout(searchTimeout.current);
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/operations/payoffs/search-clients`, { params: { q: query.trim() } });
        setResults(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(searchTimeout.current);
  }, [query, open, step]);

  const pickClient = (clientName) => {
    setSelectedClient(clientName);
    setSelectedLoanIds(null);
    setStep("review");
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // When a group offer exists and user accepted consolidation, submit by
      // client_group_id so the backend pulls all active siblings. Otherwise
      // fall back to the originally-selected loan_ids / client_name.
      const useGroup = !!(groupOffer && consolidate);
      const body = {
        client_group_id: useGroup ? groupOffer.client_group_id : null,
        client_name: !useGroup && !selectedLoanIds ? selectedClient : null,
        loan_ids: useGroup ? null : (selectedLoanIds || null),
        override_amount: overrideAmount ? Number(overrideAmount) : null,
        override_reason: overrideReason || "",
        valid_days: Number(validDays) || 10,
        notes: notes || "",
        auto_set_status: true,
      };
      const { data } = await api.post(`/operations/payoffs`, body);
      toast.success(`Payoff ${data.statement_number} generated`);
      onClose?.();
      navigate(`/admin/payoffs/${data.payoff_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setGenerating(false); }
  };

  if (!open) return null;

  const calc = preview?.calculated_payoff || 0;
  const overrideNum = overrideAmount ? Number(overrideAmount) : null;
  const finalAmt = overrideNum != null && !isNaN(overrideNum) ? overrideNum : calc;
  const savings = overrideNum != null ? calc - overrideNum : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4 bg-[#0A2540]/40 backdrop-blur-sm" data-testid="payoff-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0066FF]/10 flex items-center justify-center"><FileText className="w-4 h-4 text-[#0066FF]" /></div>
            <div>
              <div className="font-medium text-[#0A2540] text-sm">Generate payoff statement</div>
              <div className="text-[11px] text-[#6B7280]">{step === "search" ? "Search a client to begin" : selectedClient || (selectedLoanIds ? `${selectedLoanIds.length} loan(s) selected` : "")}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#F9FAFB] flex items-center justify-center text-[#6B7280]" data-testid="payoff-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {step === "search" && (
          <div className="px-6 py-5 flex-1 overflow-y-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by client name, attorney, or loan ID…"
                className="w-full h-12 pl-10 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-base placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                data-testid="payoff-search-input"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] animate-spin" />}
            </div>
            {query.trim() && !searching && results.length === 0 && (
              <div className="text-sm text-[#6B7280] text-center py-8">No matching clients.</div>
            )}
            {!query.trim() && (
              <div className="text-xs text-[#6B7280] mt-3 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Tip: start typing to instantly search across the entire portfolio.</div>
            )}
            <div className="mt-3 space-y-1">
              {results.map((r) => (
                <button
                  key={r.client_name}
                  onClick={() => pickClient(r.client_name)}
                  data-testid={`payoff-search-result-${r.client_name.replace(/\s+/g, "-")}`}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-lg border border-transparent hover:border-[#E5E7EB] hover:bg-[#F9FAFB] transition-all text-left group"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[#0A2540]">{r.client_name}</div>
                    <div className="text-xs text-[#6B7280] truncate">
                      {r.loan_count} loan{r.loan_count !== 1 ? "s" : ""} · {fmtMoney(r.total_principal)} funded · {r.attorney_emails[0] || "—"}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#0066FF]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-5">
              {groupOffer && (
                <div className="mb-4 rounded-lg border border-[#DBEAFE] bg-[#EFF6FF] p-3" data-testid="consolidation-offer">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wide">
                        {consolidate ? "Consolidated payoff" : "Single-advance payoff"}
                      </div>
                      <div className="text-sm text-[#0A2540] mt-1">
                        {groupOffer.client_name_variants?.[0] || "Client"} has <b>{groupOffer.active_count} outstanding advances</b>.
                        {consolidate
                          ? ` One statement will cover all ${groupOffer.active_count}, totaling ${fmtMoney(groupOffer.consolidated_total_active)}.`
                          : ` Only the selected advance will be included.`}
                      </div>
                      <div className="mt-2 text-[11px] text-[#374151] space-y-0.5">
                        {(groupOffer.active_advances || []).map((a) => (
                          <div key={a.loan_id} data-testid={`offer-advance-${a.loan_id}`}>
                            · Advance {a.funding_date?.slice(0, 10)} — Principal {fmtMoney(a.principal)} · Current payoff {fmtMoney(a.current_payoff)}
                            {a.is_this_loan ? <span className="ml-2 text-[#1E3A8A] font-medium">(this one)</span> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <button
                        onClick={() => setConsolidate(true)}
                        data-testid="consolidate-choice"
                        className={`text-[11px] px-2 py-1 rounded border ${consolidate ? "bg-[#0066FF] text-white border-[#0066FF]" : "bg-white border-[#D1D5DB] text-[#374151]"}`}
                      >Consolidated · default</button>
                      <button
                        onClick={() => setConsolidate(false)}
                        data-testid="single-advance-choice"
                        className={`text-[11px] px-2 py-1 rounded border ${!consolidate ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white border-[#D1D5DB] text-[#374151]"}`}
                      >Just this advance</button>
                    </div>
                  </div>
                </div>
              )}
              {preview ? (
                <>
                  <div className="rounded-xl bg-gradient-to-br from-[#0A2540] to-[#0066FF] p-5 text-white">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF]">Calculated payoff</div>
                    <div className="font-display text-3xl font-semibold tabular-nums mt-1">{fmtMoney(finalAmt)}</div>
                    <div className="text-xs text-[#BFD7FF] mt-1.5">
                      <span data-testid="payoff-preview-loan-count">{preview.loans}</span> loan{preview.loans !== 1 ? "s" : ""} · {preview.tranches} tranche{preview.tranches !== 1 ? "s" : ""} · Principal {fmtMoney(preview.total_principal)} · Accrued {fmtMoney(preview.accrued)}
                    </div>
                    {overrideNum != null && (
                      <div className="mt-3 pt-3 border-t border-white/15 text-xs text-[#BFD7FF]">
                        Negotiated reduction of <span className="text-white font-medium">{fmtMoney(savings)}</span> from calculated <span className="line-through">{fmtMoney(calc)}</span>
                      </div>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div>
                      <Label className="text-xs">Negotiated override (optional)</Label>
                      <Input type="number" step="0.01" placeholder={fmtMoney(calc)} value={overrideAmount} onChange={(e) => setOverrideAmount(e.target.value)} data-testid="payoff-override-input" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Valid through (days)</Label>
                      <Input type="number" value={validDays} onChange={(e) => setValidDays(e.target.value)} data-testid="payoff-valid-days" className="mt-1" />
                    </div>
                  </div>
                  {overrideAmount && (
                    <div className="mt-3">
                      <Label className="text-xs">Reason for override (audit log)</Label>
                      <Input placeholder="e.g. Negotiated reduction per attorney request" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="payoff-override-reason" className="mt-1" />
                    </div>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs">Internal notes (optional)</Label>
                    <Textarea rows={2} placeholder="Will not appear on PDF unless added to the notes section." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payoff-notes" className="mt-1" />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-12 text-[#6B7280]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Calculating payoff…</div>
              )}
            </div>
          </div>
        )}

        <div className="px-6 py-3 border-t border-[#E5E7EB] flex items-center justify-between bg-[#F9FAFB]">
          {step === "review" && !prefill.clientName && !prefill.loanIds ? (
            <button onClick={() => setStep("search")} className="text-xs text-[#6B7280] hover:text-[#0A2540]" data-testid="payoff-back-to-search">← Back to search</button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="h-9" data-testid="payoff-cancel">Cancel</Button>
            {step === "review" && (
              <Button onClick={handleGenerate} disabled={generating || !preview} className="bg-[#0066FF] hover:bg-[#0052CC] h-9 px-4" data-testid="payoff-generate-btn">
                {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                Generate {overrideNum != null ? "with override" : ""}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Approximate the same math the backend will run, so the modal can show a live preview.
function buildLocalPreview(loans) {
  if (!loans || loans.length === 0) return { calculated_payoff: 0, total_principal: 0, accrued: 0, loans: 0, tranches: 0 };
  let calculated = 0, principal = 0, accrued = 0, tranches = 0;
  for (const l of loans) {
    // Use already-enriched current_payoff if present (multi-tranche aware on backend)
    calculated += Number(l.current_payoff || 0);
    principal += Number(l.total_principal_all_tranches || l.principal || 0);
    accrued += Number(l.accrued_interest || 0);
    tranches += 1 + (l.additional_advances?.length || 0);
  }
  return { calculated_payoff: calculated, total_principal: principal, accrued, loans: loans.length, tranches };
}
